"use client";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { LuCalendarDays } from "react-icons/lu";
import { GoPencil } from "react-icons/go";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import "./AddAppointmentModal.css";
import CustomTimePicker from "./CustomTimePicker";
import DurationPicker from "./DurationPicker";
import CustomSelect from "./CustomSelect";
import ConfirmModal from "./ConfirmModal";
import ClientDropdown from "./ClientDropdown";
import { REST_API_URL } from "../api/config";
import { buildHeaders } from "../api/buildHeaders";
import { getCurrencySymbol } from "../utils/currency/getCurrencySymbol";
import { PrimaryButton } from "../components/buttons/primaryButton/PrimaryButton.tsx";
import { getVehicles } from "../api/dealership/dealershpService";
import { getIndustry } from "../api/profile/profileServices";

const API_URL = REST_API_URL;

const ENGLISH_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const RUSSIAN_MONTHS = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июнь",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const normalizePriceInput = (value, currencySymbol) => {
  if (!value) return "";

  let raw = String(value);

  raw = raw.replace(currencySymbol, "");
  raw = raw.replace(/,/g, ".");
  raw = raw.replace(/[^\d.]/g, "");

  const firstDotIndex = raw.indexOf(".");
  if (firstDotIndex !== -1) {
    const integerPart = raw.slice(0, firstDotIndex).replace(/\./g, "");
    const decimalPart = raw
      .slice(firstDotIndex + 1)
      .replace(/\./g, "")
      .slice(0, 2);

    raw = `${integerPart}.${decimalPart}`;
  } else {
    raw = raw.replace(/\./g, "");
  }

  if (!raw) return "";
  return `${currencySymbol}${raw}`;
};

const parsePriceToNumber = (value, currencySymbol) => {
  if (!value) return 0;

  let raw = String(value);

  raw = raw.replace(currencySymbol, "");
  raw = raw.replace(/,/g, ".");
  raw = raw.replace(/[^\d.]/g, "");

  const firstDotIndex = raw.indexOf(".");
  if (firstDotIndex !== -1) {
    const integerPart = raw.slice(0, firstDotIndex).replace(/\./g, "");
    const decimalPart = raw
      .slice(firstDotIndex + 1)
      .replace(/\./g, "")
      .slice(0, 2);

    raw = `${integerPart}.${decimalPart}`;
  } else {
    raw = raw.replace(/\./g, "");
  }

  const parsed = parseFloat(raw);
  if (Number.isNaN(parsed)) return 0;

  return Number(parsed.toFixed(2));
};

const parseDateString = (dateStr) => {
  if (!dateStr) return new Date();

  const parts = String(dateStr).trim().split(" ");
  if (parts.length < 2) return new Date();

  const monthName = parts[0];
  const day = parseInt(parts[1], 10);

  let monthIndex = ENGLISH_MONTHS.findIndex(
    (m) => m.toLowerCase() === monthName.toLowerCase(),
  );

  if (monthIndex === -1) {
    monthIndex = RUSSIAN_MONTHS.findIndex((m) =>
      monthName.toLowerCase().includes(m.toLowerCase()),
    );
  }

  return new Date(
    new Date().getFullYear(),
    monthIndex === -1 ? new Date().getMonth() : monthIndex,
    Number.isNaN(day) ? new Date().getDate() : day,
  );
};

export default function AddAppointmentModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [appointmentStarted, setAppointmentStarted] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialDataLoading, setInitialDataLoading] = useState(false);

  const startRef = useRef(null);
  const endRef = useRef(null);
  const currentWorkerIdRef = useRef(null);

  const today = new Date();
  const defaultDate = `${today.toLocaleString("en-US", {
    month: "long",
  })} ${today.getDate()}`;

  const [formData, setFormData] = useState({
    client: "",
    client_id: null,
    master: "",
    worker_id: null,
    service_type: "",
    service_id: null,
    real_estate_id: null,
    vehicle_id: null,
    service_name: "",
    is_other_service: false,
    price: "",
    date: defaultDate,
    time: "08:00",
    duration: { hours: 1, minutes: 0 },
  });

  const [workers, setWorkers] = useState([]);
  const [services, setServices] = useState([]);
  const [realEstates, setRealEstates] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [industry, setIndustry] = useState(null);

  const [userStatus, setUserStatus] = useState(null);
  const [userCurrency, setUserCurrency] = useState("USD");
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const [currentWorkerId, setCurrentWorkerId] = useState(null);
  const [currentWorkerName, setCurrentWorkerName] = useState("");

  const normalizedUserStatus = useMemo(
    () => normalizeStatus(userStatus),
    [userStatus],
  );

  const isIndividual = normalizedUserStatus === "individual";
  const isWorker = normalizedUserStatus === "worker";

  const reduxIndustry = useSelector(
    (state) => state?.profileReducer?.industryData?.industry,
  );
  const effectiveIndustry = industry || reduxIndustry || null;
  const isDealership = effectiveIndustry === "Dealerships";
  const isRealEstate = effectiveIndustry === "Sales & Real Estate";

  const filteredWorkers = useMemo(() => {
    if (formData.service_type === "service" && formData.service_id) {
      const selectedService = services.find(
        (s) => s.id === formData.service_id,
      );
      if (
        Array.isArray(selectedService?.workers) &&
        selectedService.workers.length > 0
      ) {
        return workers.filter((w) => selectedService.workers.includes(w.id));
      }
      // service selected but has no workers assigned
      return [];
    }

    if (formData.service_type === "listing" && formData.real_estate_id) {
      const selectedListing = realEstates.find(
        (r) => r.id === formData.real_estate_id,
      );
      if (
        Array.isArray(selectedListing?.workers) &&
        selectedListing.workers.length > 0
      ) {
        const listingWorkerIds = selectedListing.workers.map((w) =>
          typeof w === "object" ? w.id : w,
        );
        return workers.filter((w) => listingWorkerIds.includes(w.id));
      }
      // listing selected but has no workers assigned
      return [];
    }

    if (formData.service_type === "vehicle" && formData.vehicle_id) {
      const selectedVehicle = vehicles.find(
        (v) => v.id === formData.vehicle_id,
      );
      if (
        Array.isArray(selectedVehicle?.workers) &&
        selectedVehicle.workers.length > 0
      ) {
        const vehicleWorkerIds = selectedVehicle.workers.map((w) =>
          typeof w === "object" ? w.id : w,
        );
        return workers.filter((w) => vehicleWorkerIds.includes(w.id));
      }
      return workers;
    }

    return workers;
  }, [
    workers,
    services,
    realEstates,
    vehicles,
    formData.service_type,
    formData.service_id,
    formData.real_estate_id,
    formData.vehicle_id,
  ]);

  // true when a service/listing/vehicle is selected but has no workers assigned to it
  const selectedItemHasNoWorkers =
    (formData.service_type === "service" && formData.service_id) ||
    (formData.service_type === "listing" && formData.real_estate_id) ||
    (formData.service_type === "vehicle" && formData.vehicle_id)
      ? filteredWorkers.length === 0
      : false;
  const shouldShowMasterSelect = !isIndividual && !isWorker;

  const effectiveWorkerId =
    formData.worker_id || currentWorkerId || currentWorkerIdRef.current || null;

  const triggerStartAppointment = () => {
    if (!appointmentStarted && typeof window.gtag === "function") {
      window.gtag("event", "start_appointment_in_calendar");
      setAppointmentStarted(true);
    }
  };

  useEffect(() => {
    currentWorkerIdRef.current = currentWorkerId || null;
  }, [currentWorkerId]);

  useEffect(() => {
    if (!isOpen) return;

    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) return;

    const fetchAllData = async () => {
      setInitialDataLoading(true);

      try {
        const headers = buildHeaders();

        const userRes = await fetch(`${API_URL}/auth/userinfo/`, { headers });
        if (!userRes.ok) {
          throw new Error(t("home.toastErrorUserInfo"));
        }

        const userData = await userRes.json();

        const nextStatus = userData?.user?.status || null;
        const normalizedNextStatus = normalizeStatus(nextStatus);

        setUserStatus(nextStatus);

        const currency = userData?.currency || "USD";
        setUserCurrency(currency);
        setCurrencySymbol(getCurrencySymbol(currency));

        if (Array.isArray(userData?.workers) && userData.workers.length > 0) {
          const formattedWorkers = userData.workers.map((w) => ({
            id: w.id,
            name: w.full_name || w.email || t("home.unnamedMaster"),
            image: w.image_url || null,
            registered: Boolean(w.full_name && w.full_name.trim().length > 0),
          }));
          setWorkers(formattedWorkers);
        } else {
          setWorkers([]);
        }

        if (normalizedNextStatus === "worker") {
          const forcedWorkerId = userData?.id || null;
          const forcedWorkerName =
            `${userData?.user?.first_name || ""} ${userData?.user?.last_name || ""}`.trim() ||
            userData?.user?.email ||
            t("home.myAppointmentsFallback");

          setCurrentWorkerId(forcedWorkerId);
          currentWorkerIdRef.current = forcedWorkerId;
          setCurrentWorkerName(forcedWorkerName);

          setFormData((prev) => ({
            ...prev,
            worker_id: forcedWorkerId,
            master: forcedWorkerName,
          }));

          if (!forcedWorkerId) {
            console.error("Worker resolve failed. userData:", userData);
            toast.error(t("home.toastErrorBackendWorker"));
          }
        } else {
          setCurrentWorkerId(null);
          currentWorkerIdRef.current = null;
          setCurrentWorkerName("");
        }

        const servicesRes = await fetch(`${API_URL}/service/my-services/`, {
          headers,
        });
        if (servicesRes.ok) {
          const servicesData = await servicesRes.json();
          setServices(Array.isArray(servicesData) ? servicesData : []);
        } else {
          setServices([]);
        }

        let currentInd = null;
        try {
          const industryRes = await getIndustry();
          const ind = industryRes?.data?.industry || industryRes?.data || null;
          currentInd = typeof ind === "string" ? ind : ind?.industry || null;
          setIndustry(currentInd);
        } catch {
          // ignore error
        }

        const effectiveInd = currentInd || reduxIndustry || null;
        const checkRealEstate = effectiveInd === "Sales & Real Estate";
        const checkDealership = effectiveInd === "Dealerships";

        if (checkRealEstate) {
          const realEstateRes = await fetch(
            `${API_URL}/service/real-estate-my-listings/`,
            { headers },
          );
          if (realEstateRes.ok) {
            const realEstateData = await realEstateRes.json();
            setRealEstates(
              Array.isArray(realEstateData)
                ? realEstateData.map((estate) => ({
                    id: estate.id,
                    property_name:
                      estate.property_name || t("home.unnamedProperty"),
                    address: estate.address || t("home.noAddress"),
                    listing_type:
                      estate.listing_type || t("home.unknownType", "Unknown"),
                    property_type: estate.property_type,
                    sell_price: estate.sell_price,
                    monthly_rent_price: estate.monthly_rent_price,
                    ...estate,
                  }))
                : [],
            );
          } else {
            setRealEstates([]);
          }
        } else {
          setRealEstates([]);
        }

        if (checkDealership) {
          try {
            const vehiclesRes = await getVehicles();
            const vData = Array.isArray(vehiclesRes.data)
              ? vehiclesRes.data
              : vehiclesRes.data?.results || [];
            setVehicles(vData);
          } catch (vErr) {
            console.log("Vehicles fetch optional:", vErr);
            setVehicles([]);
          }
        } else {
          setVehicles([]);
        }
      } catch (err) {
        console.error("Error fetching appointment modal data:", err);
        toast.error(err?.message || t("home.toastErrorLoadForm"));
      } finally {
        setInitialDataLoading(false);
      }
    };

    fetchAllData();
  }, [isOpen]);

  const resetForm = () => {
    const now = new Date();

    setFormData({
      client: "",
      client_id: null,
      master: isWorker ? currentWorkerName : "",
      worker_id: isWorker ? effectiveWorkerId : null,
      service_type: "",
      service_id: null,
      real_estate_id: null,
      vehicle_id: null,
      service_name: "",
      is_other_service: false,
      price: "",
      date: `${now.toLocaleString("en-US", {
        month: "long",
      })} ${now.getDate()}`,
      time: "08:00",
      duration: { hours: 1, minutes: 0 },
    });

    setOpenDropdown(null);
  };

  const getCurrentServiceListingValue = () => {
    if (formData.service_type === "service" && formData.service_id) {
      return `service_${formData.service_id}`;
    }
    if (formData.service_type === "listing" && formData.real_estate_id) {
      return `listing_${formData.real_estate_id}`;
    }
    if (formData.service_type === "vehicle" && formData.vehicle_id) {
      return `vehicle_${formData.vehicle_id}`;
    }
    if (formData.service_type === "other") {
      return "other";
    }
    return "";
  };

  const getServiceListingOptions = () => {
    const placeholderLabel = isDealership
      ? t("home.placeholderSelectVehicleOrService", {
          defaultValue: "Select vehicle or service",
        })
      : isRealEstate
        ? t("home.placeholderSelectPropertyOrService", {
            defaultValue: "Select property or service",
          })
        : t("home.placeholderSelectServiceListing", {
            defaultValue: "Select service",
          });

    const options = [
      {
        value: "",
        label: placeholderLabel,
        type: "placeholder",
        className: "custom-select-placeholder",
      },
    ];

    const activeServices = services.filter(
      (service) => service.on_off === true,
    );

    const otherOption = {
      value: "other",
      label: t("home.optionCustomService", {
        defaultValue: "Other (custom service)",
      }),
      type: "other",
      className: "custom-select-other",
    };

    if (isRealEstate || isDealership) {
      options.push({
        value: "services_header",
        label: t("yourServices", {
          defaultValue: t("home.headerYourServices", {
            defaultValue: "Your Services",
          }),
        }),
        type: "header",
        isDisabled: true,
        align: "center",
        className: "custom-select-header static-header",
      });

      activeServices.forEach((service) => {
        options.push({
          value: `service_${service.id}`,
          label:
            service.name ||
            service.title ||
            t("home.unnamedProperty", { defaultValue: "Service" }),
          type: "service",
          originalData: service,
          className: "custom-select-service",
        });
      });

      // "Other (custom service)" belongs strictly to services
      options.push(otherOption);

      if (isRealEstate && realEstates.length > 0) {
        options.push({
          value: "listings_header",
          label: t("bottomSheet.yourListings", {
            defaultValue: t("home.headerYourListings", {
              defaultValue: "Your Listings",
            }),
          }),
          type: "header",
          isDisabled: true,
          align: "center",
          className: "custom-select-header static-header",
        });

        realEstates.forEach((estate) => {
          options.push({
            value: `listing_${estate.id}`,
            label: estate.property_name,
            type: "listing",
            originalData: estate,
            className: "custom-select-listing",
          });
        });
      }

      if (isDealership && vehicles.length > 0) {
        options.push({
          value: "vehicles_header",
          label: t("bottomSheet.yourVehicles", {
            defaultValue: t("home.headerYourVehicles", {
              defaultValue: "Your Vehicles",
            }),
          }),
          type: "header",
          isDisabled: true,
          align: "center",
          className: "custom-select-header static-header",
        });

        vehicles.forEach((vehicle) => {
          const vehicleTitle =
            `${vehicle.make_name || ""} ${vehicle.model_name || ""}`.trim() ||
            (vehicle.year ? `${vehicle.year} Vehicle` : "Vehicle");
          options.push({
            value: `vehicle_${vehicle.id}`,
            label: vehicleTitle,
            type: "vehicle",
            originalData: vehicle,
            className: "custom-select-vehicle",
          });
        });
      }
    } else {
      // Standard service business: direct flat list without any section headers
      activeServices.forEach((service) => {
        options.push({
          value: `service_${service.id}`,
          label:
            service.name ||
            service.title ||
            t("home.unnamedProperty", { defaultValue: "Service" }),
          type: "service",
          originalData: service,
          className: "custom-select-service",
        });
      });

      // "Other (custom service)"
      options.push(otherOption);
    }

    return options;
  };

  const handleServiceListingChange = (option) => {
    const { value, type, originalData } = option;

    triggerStartAppointment();

    if (value === "") {
      setFormData((prev) => ({
        ...prev,
        service_type: "",
        service_id: null,
        real_estate_id: null,
        vehicle_id: null,
        service_name: "",
        is_other_service: false,
        price: "",
        duration: { hours: 1, minutes: 0 },
      }));
      return;
    }

    const defaultWorkerId = isWorker ? effectiveWorkerId : null;
    const defaultMaster = isWorker ? currentWorkerName : "";

    // Duration calculation matching mobile onPress:
    // (item?.duration || item?.tour_duration || item?.viewing_duration) ?? 15
    const rawDuration =
      Number(
        originalData?.duration ??
          originalData?.tour_duration ??
          originalData?.viewing_duration,
      ) || 15;

    const timeUnit =
      originalData?.time_unit ||
      originalData?.tour_duration_time_unit ||
      originalData?.viewing_duration_time_unit ||
      "minutes";

    let durationHours = 0;
    let durationMins = 15;
    if (timeUnit === "hours") {
      durationHours = rawDuration;
      durationMins = 0;
    } else {
      durationHours = Math.floor(rawDuration / 60);
      durationMins = rawDuration % 60;
    }

    // Price calculation matching mobile onPress:
    // item?.price || item?.monthly_rent_price || item?.sell_price || item?.yearly_rent_price || item?.leasing_price ?? "0"
    let rawPrice = "";
    if (type === "service") {
      rawPrice = originalData?.price;
    } else if (type === "listing") {
      if (originalData?.listing_type?.toLowerCase() === "rent") {
        rawPrice = originalData?.monthly_rent_price || originalData?.yearly_rent_price || originalData?.price;
      } else if (originalData?.listing_type?.toLowerCase() === "sale") {
        rawPrice = originalData?.sell_price || originalData?.price;
      } else {
        rawPrice =
          originalData?.sell_price ||
          originalData?.monthly_rent_price ||
          originalData?.yearly_rent_price ||
          originalData?.price;
      }
    } else if (type === "vehicle") {
      rawPrice =
        originalData?.price !== undefined && originalData?.price !== null && originalData?.price !== ""
          ? originalData?.price
          : originalData?.leasing_price || "";
    }

    let priceStr = "";
    if (rawPrice !== undefined && rawPrice !== null && rawPrice !== "") {
      const priceNum = Number(rawPrice);
      priceStr = !Number.isNaN(priceNum)
        ? `${currencySymbol}${priceNum.toFixed(2)}`
        : `${currencySymbol}0.00`;
    } else {
      priceStr = `${currencySymbol}0.00`;
    }

    switch (type) {
      case "service": {
        const service = originalData;
        setFormData((prev) => ({
          ...prev,
          service_type: "service",
          service_id: service.id,
          real_estate_id: null,
          vehicle_id: null,
          service_name:
            service.name ||
            service.title ||
            t("home.unnamedProperty", { defaultValue: "Service" }),
          is_other_service: false,
          price: priceStr,
          duration: { hours: durationHours, minutes: durationMins },
          worker_id: defaultWorkerId,
          master: defaultMaster,
        }));
        break;
      }

      case "listing": {
        const estate = originalData;
        setFormData((prev) => ({
          ...prev,
          service_type: "listing",
          service_id: null,
          real_estate_id: estate.id,
          vehicle_id: null,
          service_name: estate.property_name,
          is_other_service: false,
          price: priceStr,
          duration: { hours: durationHours, minutes: durationMins },
          worker_id: defaultWorkerId,
          master: defaultMaster,
        }));
        break;
      }

      case "vehicle": {
        const vehicle = originalData;
        const vehicleTitle =
          `${vehicle.make_name || ""} ${vehicle.model_name || ""}`.trim() ||
          (vehicle.year ? `${vehicle.year} Vehicle` : "Vehicle");

        setFormData((prev) => ({
          ...prev,
          service_type: "vehicle",
          service_id: null,
          real_estate_id: null,
          vehicle_id: vehicle.id,
          service_name: vehicleTitle,
          is_other_service: false,
          price: priceStr,
          duration: { hours: durationHours, minutes: durationMins },
          worker_id: defaultWorkerId,
          master: defaultMaster,
        }));
        break;
      }

      case "other":
        setFormData((prev) => ({
          ...prev,
          service_type: "other",
          service_id: null,
          real_estate_id: null,
          vehicle_id: null,
          service_name: "",
          is_other_service: true,
          price: "",
          duration: { hours: 0, minutes: 15 },
          worker_id: defaultWorkerId,
          master: defaultMaster,
        }));
        break;

      default:
        break;
    }
  };

  const handlePriceChange = (e) => {
    const normalized = normalizePriceInput(e.target.value, currencySymbol);

    setFormData((prev) => ({
      ...prev,
      price: normalized,
    }));

    triggerStartAppointment();
  };

  const sendAppointment = async (confirmed) => {
    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) throw new Error("No access token");
    if (!startRef.current || !endRef.current) {
      throw new Error("Invalid appointment times");
    }

    const totalDuration =
      (formData.duration?.hours || 0) * 60 + (formData.duration?.minutes || 0);

    if (totalDuration <= 0) {
      throw new Error("Duration must be greater than 0");
    }

    const tzOffset = startRef.current.getTimezoneOffset() * 60000;
    const priceValue = parsePriceToNumber(formData.price, currencySymbol);

    const startDate = new Date(startRef.current.getTime() - tzOffset);
    startDate.setSeconds(0, 0);
    const endDate = new Date(endRef.current.getTime() - tzOffset);
    endDate.setSeconds(0, 0);

    const body = {
      price: priceValue,
      currency: userCurrency || "USD",
      appointment_date_time: startDate.toISOString(),
      appointment_end_time: endDate.toISOString(),
      duration: totalDuration,
      time_unit: "minutes",
      confirmed,
    };

    if (formData.service_type === "service" && formData.service_id) {
      body.service = formData.service_id;
    } else if (formData.service_type === "listing" && formData.real_estate_id) {
      body.listing = formData.real_estate_id;
    } else if (formData.service_type === "vehicle" && formData.vehicle_id) {
      body.vehicle = formData.vehicle_id;
    } else if (formData.service_type === "other") {
      if (!formData.service_name || !formData.service_name.trim()) {
        throw new Error(
          t("home.validationErrorCustomServiceName", {
            defaultValue: "Service name is required for custom service",
          }),
        );
      }
      body.is_other_service = true;
      body.new_service_name = formData.service_name.trim();
    } else {
      throw new Error(
        t("home.validationErrorServiceSelection", {
          defaultValue: "Service or item must be selected",
        }),
      );
    }

    if (formData.client_id) {
      body.client = formData.client_id;
    } else if (formData.client && formData.client.trim()) {
      body.new_client = true;
      body.client_name = formData.client.trim();
    } else {
      throw new Error("Client information is required");
    }

    if (isWorker) {
      if (!effectiveWorkerId) {
        throw new Error("Current worker could not be identified");
      }

      body.workers = [effectiveWorkerId];
    } else if (!isIndividual) {
      if (!formData.worker_id) {
        throw new Error("Master must be selected");
      }

      body.workers = [formData.worker_id];
    }

    const headers = buildHeaders();

    const res = await fetch(`${API_URL}/client/appointments/`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    const text = await res.text();
    let data = null;

    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw {
        code: 500,
        message: "Server returned invalid response",
        rawResponse: text,
      };
    }

    if (!res.ok) {
      throw data;
    }

    return data;
  };

  const handleConfirmOverlap = async () => {
    try {
      setConfirmModalOpen(false);
      setLoading(true);
      await sendAppointment(true);
      toast.success(t("home.toastSuccessCreated"));
      resetForm();
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("Confirm overlap error:", err);
      toast.error(err?.message || err?.detail || t("home.toastErrorOverlap"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (initialDataLoading) {
      toast.error(t("home.toastErrorWaitWorker"));
      return;
    }

    const errors = [];

    if (!formData.client.trim() && !formData.client_id) {
      errors.push(t("home.validationErrorClient"));
    }

    const parsedPrice = parsePriceToNumber(formData.price, currencySymbol);

    if (!formData.price.trim()) {
      errors.push(t("home.validationErrorPriceEmpty"));
    } else if (Number.isNaN(parsedPrice) || parsedPrice <= 0) {
      errors.push(t("home.validationErrorPriceInvalid"));
    }

    if (!formData.date) errors.push(t("home.validationErrorDate"));
    if (!formData.time) errors.push(t("home.validationErrorTime"));

    const totalDuration =
      (formData.duration?.hours || 0) * 60 + (formData.duration?.minutes || 0);

    if (totalDuration <= 0) {
      errors.push(t("home.validationErrorDuration"));
    }

    if (shouldShowMasterSelect && !formData.worker_id) {
      errors.push(t("home.validationErrorMaster"));
    }

    if (isWorker && !effectiveWorkerId) {
      errors.push(t("home.validationErrorWorkerIdentify"));
    }

    if (!formData.service_type) {
      errors.push(t("home.validationErrorServiceSelection"));
    }

    if (formData.service_type === "other" && !formData.service_name.trim()) {
      errors.push(t("home.validationErrorCustomServiceName"));
    }

    if (errors.length > 0) {
      console.error("Appointment validation errors:", errors);
      toast.error(errors[0], { duration: 5000 });
      return;
    }

    const dateObj = parseDateString(formData.date || defaultDate);
    const [hours, minutes] = (formData.time || "08:00").split(":").map(Number);

    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      toast.error(t("home.validationErrorTimeRange"));
      return;
    }

    dateObj.setHours(hours, minutes, 0, 0);

    const start = new Date(dateObj);
    if (Number.isNaN(start.getTime())) {
      toast.error(t("home.validationErrorDateTimeObj"));
      return;
    }

    const durationH = formData.duration?.hours || 0;
    const durationM = formData.duration?.minutes || 0;
    const end = new Date(
      start.getTime() + (durationH * 60 + durationM) * 60000,
    );

    startRef.current = start;
    endRef.current = end;

    setLoading(true);
    try {
      await sendAppointment(false);
      toast.success(t("home.toastSuccessCreated"));
      resetForm();
      onSuccess?.();
      onClose?.();
    } catch (err) {
      if (err?.code === 412 || err?.code === 414) {
        setConfirmMessage(err.message || t("home.toastErrorOverlap"));
        setConfirmModalOpen(true);
      } else if (err?.code === 410) {
        toast.error(err.message || t("home.toastErrorOverlap"));
      } else if (err?.price?.[0]) {
        toast.error(err.price[0]);
      } else if (err?.workers?.[0]) {
        toast.error(err.workers[0]);
      } else {
        toast.error(err?.message || err?.detail || t("home.toastErrorOverlap"));
      }
    } finally {
      setLoading(false);
    }
  };

  function CalendarDropdown({
    id,
    openDropdown,
    setOpenDropdown,
    value,
    onChange,
  }) {
    const getEnglishMonth = (dateString) => {
      if (!dateString) return ENGLISH_MONTHS[today.getMonth()];

      const monthName = dateString.split(" ")[0];
      const foundMonth = ENGLISH_MONTHS.find(
        (m) => m.toLowerCase() === monthName.toLowerCase(),
      );

      if (foundMonth) return foundMonth;

      const russianIndex = RUSSIAN_MONTHS.findIndex((m) =>
        monthName.toLowerCase().includes(m.toLowerCase()),
      );

      return russianIndex !== -1
        ? ENGLISH_MONTHS[russianIndex]
        : ENGLISH_MONTHS[today.getMonth()];
    };

    const getDayFromDate = (dateString) => {
      if (!dateString) return today.getDate();
      const parts = dateString.split(" ");
      const day = parseInt(parts[1], 10);
      return Number.isNaN(day) ? today.getDate() : day;
    };

    const [selectedMonth, setSelectedMonth] = useState(getEnglishMonth(value));
    const [selectedDay, setSelectedDay] = useState(getDayFromDate(value));
    const isOpenDropdown = openDropdown === id;

    useEffect(() => {
      setSelectedMonth(getEnglishMonth(value));
      setSelectedDay(getDayFromDate(value));
    }, [value]);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (!e.target.closest(".calendar-dropdown")) {
          setOpenDropdown(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [setOpenDropdown]);

    const toggleOpen = () => setOpenDropdown(isOpenDropdown ? null : id);

    const handleSave = () => {
      setOpenDropdown(null);
      onChange(`${selectedMonth} ${selectedDay}`);
    };

    return (
      <div className="appointment-page calendar-dropdown">
        <div className="appointment-page calendar-input" onClick={toggleOpen}>
          <span className="appointment-page icon">
            <LuCalendarDays />
          </span>
          <input
            type="text"
            value={`${t(`home.month${selectedMonth}`)} ${selectedDay}`}
            readOnly
          />
          <span className="appointment-page edit-icon">
            <GoPencil />
          </span>
        </div>

        <AnimatePresence>
          {isOpenDropdown && (
            <motion.div
              className="appointment-page calendar-popup"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="appointment-page month-selector">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {ENGLISH_MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {t(`home.month${m}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="appointment-page calendar-days">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <div
                    key={d}
                    className={`appointment-page day ${
                      d === selectedDay ? "active" : ""
                    }`}
                    onClick={() => setSelectedDay(d)}
                  >
                    {d}
                  </div>
                ))}
              </div>

              <div className="appointment-page calendar-buttons">
                <PrimaryButton
                  type="button"
                  className="appointment-page cancel"
                  onClick={() => setOpenDropdown(null)}
                  label={t("home.buttonCancel")}
                  variant="ghost"
                  size="small"
                />
                <PrimaryButton
                  type="button"
                  className="appointment-page save"
                  onClick={handleSave}
                  label={t("home.buttonSave")}
                  size="small"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="appointment-page modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="appointment-page modal"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
          >
            <div className="appointment-page modal-header">
              <h2>{t("home.addAppointmentTitle")}</h2>
              <PrimaryButton
                type="button"
                onClick={onClose}
                label=""
                leftIcon={<XMarkIcon className="w-6 h-6" />}
                variant="ghost"
                size="small"
              />
            </div>

            <div className="appointment-page form">
              <div className="appointment-page form-row">
                <div className="appointment-page form-group">
                  <label>{t("home.labelClientName")}</label>
                  <ClientDropdown
                    value={formData.client}
                    clientId={formData.client_id}
                    onChange={(name, id) => {
                      triggerStartAppointment();
                      setFormData((prev) => ({
                        ...prev,
                        client: name,
                        client_id: id,
                      }));
                    }}
                  />
                </div>

                {shouldShowMasterSelect &&
                  (() => {
                    const isOther = formData.service_type === "other";
                    const isServiceOrListingSelected =
                      (formData.service_type === "service" &&
                        formData.service_id) ||
                      (formData.service_type === "listing" &&
                        formData.real_estate_id) ||
                      (formData.service_type === "vehicle" &&
                        formData.vehicle_id);

                    // Nothing selected at all — disabled
                    if (!isServiceOrListingSelected && !isOther) {
                      return (
                        <div className="appointment-page form-group master-field">
                          <CustomSelect
                            label={t("home.labelMaster")}
                            placeholder={t("home.placeholderSelectMaster")}
                            value=""
                            onChange={() => {}}
                            options={[]}
                            disabled={true}
                          />
                          <p
                            style={{
                              marginTop: 4,
                              fontSize: 12,
                              color: "#9CA3AF",
                            }}
                          >
                            {t("home.selectServiceFirst", {
                              defaultValue:
                                "Select a service, listing, or vehicle first",
                            })}
                          </p>
                        </div>
                      );
                    }

                    // Service/listing selected but no workers assigned
                    if (!isOther && selectedItemHasNoWorkers) {
                      return (
                        <div className="appointment-page form-group master-field">
                          <label
                            style={{
                              display: "block",
                              marginBottom: 6,
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#374151",
                            }}
                          >
                            {t("home.labelMaster")}
                          </label>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "10px 14px",
                              borderRadius: 10,
                              background: "#FFF7ED",
                              border: "1px solid #FED7AA",
                              color: "#92400E",
                              fontSize: 13,
                              fontWeight: 500,
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ flexShrink: 0 }}
                            >
                              <circle cx="12" cy="12" r="10" />
                              <line x1="12" y1="8" x2="12" y2="12" />
                              <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            {t("home.noWorkersAssigned", {
                              defaultValue: "No workers assigned to this item",
                            })}
                          </div>
                        </div>
                      );
                    }

                    // "Other" or service/listing with workers — show full/filtered list
                    const workerOptions = isOther ? workers : filteredWorkers;

                    return (
                      <div className="appointment-page form-group master-field">
                        <CustomSelect
                          label={t("home.labelMaster")}
                          placeholder={t("home.placeholderSelectMaster")}
                          value={formData.worker_id || ""}
                          onChange={(option) => {
                            triggerStartAppointment();
                            setFormData((prev) => ({
                              ...prev,
                              worker_id: option.value,
                              master: option.label || "",
                            }));
                          }}
                          options={workerOptions.map((w) => ({
                            value: w.id,
                            label: w.registered
                              ? w.name
                              : `${w.name} ${t("home.notRegisteredSuffix")}`,
                            disabled: !w.registered,
                          }))}
                        />
                      </div>
                    );
                  })()}
              </div>

              <div className="appointment-page form-group">
                <CustomSelect
                  label={t("home.labelServiceListingType", {
                    defaultValue: t("description.serviceDescription", {
                      defaultValue: "Service description",
                    }),
                  })}
                  placeholder={
                    isDealership
                      ? t("home.placeholderSelectVehicleOrService", {
                          defaultValue: "Select vehicle or service",
                        })
                      : isRealEstate
                        ? t("home.placeholderSelectPropertyOrService", {
                            defaultValue: "Select property or service",
                          })
                        : t("home.placeholderSelectServiceListing", {
                            defaultValue: "Select service",
                          })
                  }
                  value={getCurrentServiceListingValue()}
                  onChange={handleServiceListingChange}
                  options={getServiceListingOptions()}
                  searchable={true}
                  searchPlaceholder={t("search", { defaultValue: "Search" })}
                />
              </div>

              {formData.service_type === "other" && (
                <div className="appointment-page form-group">
                  <label>{t("home.labelServiceName")}</label>
                  <input
                    type="text"
                    placeholder={t("home.placeholderEnterServiceName")}
                    value={formData.service_name}
                    className="input-cust"
                    onChange={(e) => {
                      triggerStartAppointment();
                      setFormData((prev) => ({
                        ...prev,
                        service_name: e.target.value,
                      }));
                    }}
                  />
                </div>
              )}

              <div className="appointment-page form-row">
                <div className="appointment-page form-group">
                  <label>{t("home.labelPrice")}</label>
                  <input
                    type="text"
                    placeholder={`${currencySymbol}30.00`}
                    value={formData.price}
                    className="input-cust"
                    onChange={handlePriceChange}
                  />
                  <small className="text-gray-500 text-xs mt-1 block">
                    {formData.service_type === "service"
                      ? t("home.hintServicePriceAuto", {
                          currencyCode: userCurrency,
                          currencySymbol,
                        })
                      : formData.service_type === "listing"
                        ? t("home.hintListingPriceAuto", {
                            currencyCode: userCurrency,
                            currencySymbol,
                          })
                        : formData.service_type === "vehicle"
                          ? t("home.hintVehiclePriceAuto", {
                              defaultValue:
                                "The vehicle price is automatically filled based on the selected vehicle.",
                              currencyCode: userCurrency,
                              currencySymbol,
                            })
                          : formData.service_type === "other"
                            ? t("home.hintCustomPriceManual", {
                                currencyCode: userCurrency,
                                currencySymbol,
                              })
                            : t("home.hintPriceEmptyState", {
                                currencyCode: userCurrency,
                                currencySymbol,
                              })}
                  </small>
                </div>
              </div>

              <div className="appointment-page form-row">
                <div className="appointment-page form-group">
                  <label>{t("home.labelDate")}</label>
                  <CalendarDropdown
                    id="calendar"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    value={formData.date}
                    onChange={(value) => {
                      triggerStartAppointment();
                      setFormData((prev) => ({ ...prev, date: value }));
                    }}
                  />
                </div>

                <div className="appointment-page form-group">
                  <label>{t("home.labelStartTime")}</label>
                  <CustomTimePicker
                    id="time"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    value={formData.time}
                    onChange={(value) => {
                      triggerStartAppointment();
                      setFormData((prev) => ({ ...prev, time: value }));
                    }}
                  />
                </div>

                <div className="appointment-page form-group">
                  <label>{t("home.labelDuration")}</label>
                  <DurationPicker
                    id="duration"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                    value={formData.duration}
                    onChange={(value) => {
                      triggerStartAppointment();
                      setFormData((prev) => ({ ...prev, duration: value }));
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="appointment-page modal-footer">
              <PrimaryButton
                type="button"
                onClick={onClose}
                label={t("home.buttonCancel")}
                variant="ghost"
              />
              <PrimaryButton
                type="button"
                onClick={() => {
                  handleSubmit();
                }}
                disabled={loading || initialDataLoading}
                label={
                  initialDataLoading
                    ? t("home.stateLoading")
                    : loading
                      ? t("home.stateSaving")
                      : t("home.buttonCreate")
                }
              />
            </div>
          </motion.div>

          <ConfirmModal
            isOpen={confirmModalOpen}
            onClose={() => setConfirmModalOpen(false)}
            onConfirm={handleConfirmOverlap}
            message={confirmMessage}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
